import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import type { ColumnDef } from "@tanstack/react-table";
import {
  EntityQuery,
  ManagedFile,
  Query,
  getEntityType,
  is,
} from "@the-real-insight/tri-model";

import { DataTable } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Accept } from "react-dropzone";
import DocumentUploadArea from "./DocumentUploadArea";
import DocumentViewerDialog from "./DocumentViewerDialog";
import { userAPI } from "../UserAPI";
import { useAdministrationAPI } from "../AdministrationAPI";
import { authenticatedFetch } from "../JWTAuthService";
import { configuration } from "../Configuration";
import { useModalContext } from "./ModalProvider";
import {
  formatBytes,
  inferTypeFromName,
  PdfPreview,
  typeIconFor,
} from "./DocumentPanelShared";

// Reusable documents tab shared by Asset / Building / Floor / Space property
// panels. It lists the ManagedFiles referenced by `entity.documents`, lets the
// user drop new files (cached locally with a per-file role until uploaded), and
// persists them via POST /entities/:type/:id/documents. Read-only `extraDocuments`
// (e.g. an asset's product documents) can be shown alongside the entity's own.

const ACCEPT: Accept = {
  "application/pdf": [".pdf"],
  "application/msword": [".doc"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
    ".docx",
  ],
  "application/vnd.ms-excel": [".xls"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
};

const ROLE_KEYS = [
  "manual",
  "datasheet",
  "certificate",
  "approval",
  "drawing",
  "other",
] as const;

/** A read-only document descriptor (e.g. product documents shown on an asset). */
export interface ExtraDocument {
  type: string;
  synopsis: string;
  url: string;
}

interface DocRow {
  id: string;
  name: string;
  size?: number;
  type: string;
  role: string;
  uploadedAt?: string;
  /** Object URL for not-yet-uploaded (buffered) files. */
  previewUrl?: string;
  /** Server path for persisted/managed files, used for authenticated fetch. */
  url?: string;
  source: "managed" | "extra";
}

const docSegments = (path = "") => path.split("/").filter(Boolean);

interface Props {
  /** Entity type name, e.g. "Asset", "Building", "Floor", "Space". */
  entityType: string;
  /** The entity object; must expose `_id` and a `documents` array of ManagedFile ids. */
  entity: any;
  /** Notifies the parent with the entity merged with its updated `documents` array. */
  onChange?: (updatedEntity: any) => void;
  /** Read-only documents listed alongside the entity's own (e.g. product docs). */
  extraDocuments?: ExtraDocument[];
  /** Open handler for `extraDocuments` rows. */
  onOpenExtraDocument?: (url: string) => void;
  /** List and open only — no upload, no delete (e.g. a manufacturer viewing a foreign asset). */
  readOnly?: boolean;
  /** Appended to each managed-file URL, e.g. `?asset=<id>` for the manufacturer grant. */
  fileQuery?: string;
}

export default function EntityDocumentsPanel({
  entityType,
  entity,
  onChange,
  extraDocuments,
  onOpenExtraDocument,
  readOnly = false,
  fileQuery = "",
}: Props) {
  const { t } = useTranslation();
  const { pushError, pushMessage } = useModalContext() as any;
  const { uploadEntityDocuments, updateEntity } = useAdministrationAPI();

  const [managedFiles, setManagedFiles] = useState<any[]>([]);
  const [buffered, setBuffered] = useState<{ file: File; role: string }[]>([]);
  const [uploading, setUploading] = useState(false);
  /*
   * PDFs und Bilder öffnen im Betrachter wie im Datenraum, nicht in einem neuen Tab: Gemessen am
   * 16.10.2026 öffnete der Dokumente-Reiter einer Anlage jedes Dokument per `window.open` — ein
   * Blob-Tab ohne Dateinamen, während der Datenraum dasselbe PDF im Betrachter zeigt.
   */
  const [viewer, setViewer] = useState<{ url: string; name: string; kind: "pdf" | "image" } | null>(null);
  const closeViewer = useCallback(() => {
    setViewer((current) => {
      if (current) URL.revokeObjectURL(current.url);
      return null;
    });
  }, []);

  const documentIds: string[] = useMemo(
    () =>
      (entity?.documents || [])
        .map((id: any) => (id ? String(id) : ""))
        .filter(Boolean),
    [entity?.documents],
  );

  // Resolve each ManagedFile by _id one-by-one so each query hits tri-server's
  // direct _id lookup (an OR over multiple ids does not coerce to ObjectId).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (documentIds.length === 0) {
        setManagedFiles([]);
        return;
      }
      try {
        const rows = await Promise.all(
          documentIds.map(async (id) => {
            const result = await userAPI.query(
              new Query(new EntityQuery(ManagedFile.type)).where(
                (is("_id") as any).equal(id),
              ),
            );
            return Array.isArray(result) && result.length > 0 ? result[0] : null;
          }),
        );
        if (!cancelled) setManagedFiles(rows.filter(Boolean));
      } catch (err) {
        if (!cancelled) pushError(err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [documentIds, pushError]);

  const addBuffered = useCallback((files: File[]) => {
    setBuffered((prev) => [
      ...prev,
      ...files.map((file) => ({ file, role: "other" })),
    ]);
  }, []);

  const setBufferedRole = useCallback((index: number, role: string) => {
    setBuffered((prev) =>
      prev.map((b, i) => (i === index ? { ...b, role } : b)),
    );
  }, []);

  const removeBuffered = useCallback((index: number) => {
    setBuffered((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const uploadBuffered = useCallback(async () => {
    if (!entity?._id || buffered.length === 0) return;
    setUploading(true);
    try {
      const { documents } = await uploadEntityDocuments(
        entityType,
        String(entity._id),
        buffered,
      );
      setBuffered([]);
      onChange?.({ ...entity, documents });
    } catch (err) {
      pushError(err);
    } finally {
      setUploading(false);
    }
  }, [entity, buffered, entityType, uploadEntityDocuments, onChange, pushError]);

  const openManaged = useCallback(
    async (url?: string, name = "") => {
      if (!url) return;
      try {
        const response = await authenticatedFetch(
          `${configuration.serverUrl}${url}`,
          { method: "GET", headers: { Accept: "*/*" } },
        );
        if (!response.ok) {
          // A failed fetch used to go to pushError, which renders the generic
          // "An error has occured." for anything that is not an ApplicationError —
          // so a 403 on every building document (tri-server #732) was indistinguishable
          // from a network fault, and neither the customer nor the log said which.
          pushMessage(
            response.status === 403
              ? t("entityDocuments.openForbidden")
              : t("entityDocuments.openFailed", { status: response.status }),
          );
          return;
        }
        const blob = await response.blob();
        const contentType =
          response.headers.get("content-type") || "application/octet-stream";
        const blobUrl = URL.createObjectURL(
          new Blob([blob], { type: contentType }),
        );
        const kind = /pdf/i.test(contentType) || /\.pdf$/i.test(name)
          ? "pdf"
          : /^image\//i.test(contentType)
            ? "image"
            : null;
        if (kind) {
          setViewer({ url: blobUrl, name, kind });
          return;
        }
        window.open(blobUrl, "_blank");
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
      } catch (err) {
        pushError(err);
      }
    },
    [pushError, pushMessage, t],
  );

  const removeManaged = useCallback(
    async (id: string) => {
      if (!entity?._id) return;
      try {
        const nextDocuments = documentIds.filter((d) => d !== id);
        const EntityClass: any = getEntityType(entityType);
        await updateEntity(
          new EntityClass({ ...entity, documents: nextDocuments }),
        );
        onChange?.({ ...entity, documents: nextDocuments });
      } catch (err) {
        pushError(err);
      }
    },
    [entity, documentIds, entityType, updateEntity, onChange, pushError],
  );

  const rows = useMemo<DocRow[]>(() => {
    const managedRows: DocRow[] = managedFiles.map((mf: any) => {
      const segs = docSegments(mf.path || mf.name || "");
      let filename = segs[segs.length - 1] || "document";
      try {
        filename = decodeURIComponent(filename);
      } catch {
        // Keep the raw filename if decoding fails
      }
      const role =
        (Array.isArray(mf.roles) && mf.roles[0]) ||
        segs[segs.length - 2] ||
        "other";
      return {
        id: String(mf._id),
        name: filename,
        size: mf.size,
        type: inferTypeFromName(filename),
        role: String(role).toLowerCase(),
        uploadedAt: mf.created || mf.updated,
        url: `/managed-files/${mf._id}${fileQuery}`,
        source: "managed",
      };
    });
    const extraRows: DocRow[] = (extraDocuments || []).map((d) => ({
      id: `extra-${d.url}`,
      name: d.synopsis,
      type: inferTypeFromName(d.synopsis),
      role: d.type,
      url: d.url,
      source: "extra",
    }));
    return [...extraRows, ...managedRows];
  }, [managedFiles, extraDocuments, fileQuery]);

  const roleLabel = useCallback(
    (role: string) => {
      const key = role.toLowerCase();
      return (ROLE_KEYS as readonly string[]).includes(key)
        ? t(`assetPropertiesPanel.documents.role.${key}`)
        : role;
    },
    [t],
  );

  const openRow = useCallback(
    (row: DocRow) => {
      // Product documents are managed files too: same viewer. Only other links go to the parent.
      if (row.source === "extra" && !row.url?.startsWith("/managed-files/")) {
        if (row.url && onOpenExtraDocument) onOpenExtraDocument(row.url);
      } else {
        openManaged(row.url, row.name);
      }
    },
    [onOpenExtraDocument, openManaged],
  );

  const columns = useMemo<ColumnDef<DocRow>[]>(
    () => [
      {
        accessorKey: "name",
        header: t("assetPropertiesPanel.documents.columnName"),
        cell: ({ row }) => (
          <div className="truncate" title={row.original.name}>
            {row.original.name}
          </div>
        ),
      },
      {
        accessorKey: "type",
        header: t("assetPropertiesPanel.documents.columnType"),
        size: 110,
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            {typeIconFor(row.original.type)}
            <span className="text-sm">{row.original.type}</span>
          </div>
        ),
      },
      {
        accessorKey: "size",
        header: t("assetPropertiesPanel.documents.columnSize"),
        size: 110,
        cell: ({ row }) => formatBytes(row.original.size),
      },
      {
        accessorKey: "uploadedAt",
        header: t("assetPropertiesPanel.documents.columnUploadedAt"),
        size: 170,
        cell: ({ row }) =>
          row.original.uploadedAt
            ? dayjs(row.original.uploadedAt).format("DD.MM.YYYY HH:mm")
            : "—",
      },
      {
        id: "role",
        header: t("assetPropertiesPanel.documents.columnRole"),
        size: 180,
        cell: ({ row }) => (
          <div className="text-sm">{roleLabel(row.original.role)}</div>
        ),
      },
      {
        id: "preview",
        header: t("assetPropertiesPanel.documents.columnPreview"),
        size: 130,
        cell: ({ row }) => {
          const onClick = () => openRow(row.original);
          if (row.original.type === "PDF") {
            return (
              <div
                role="button"
                tabIndex={0}
                onClick={onClick}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") onClick();
                }}
                className="relative cursor-pointer"
                style={{ width: 100, height: 140 }}
                title={row.original.name}
              >
                <PdfPreview url={row.original.url} name={row.original.name} />
                <div
                  className="absolute inset-0"
                  style={{ background: "transparent" }}
                />
              </div>
            );
          }
          return (
            <div
              role="button"
              tabIndex={0}
              onClick={onClick}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") onClick();
              }}
              className="flex cursor-pointer items-center justify-center rounded-md border bg-muted text-xs text-muted-foreground hover:bg-muted/70"
              style={{ width: 100, height: 140 }}
              title={row.original.name}
            >
              {row.original.type}
            </div>
          );
        },
      },
      {
        id: "actions",
        header: t("assetPropertiesPanel.documents.columnActions"),
        size: 110,
        cell: ({ row }) =>
          row.original.source === "managed" && !readOnly ? (
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={() => removeManaged(row.original.id)}
            >
              {t("global.delete")}
            </Button>
          ) : null,
      },
    ],
    [t, roleLabel, openRow, removeManaged, readOnly],
  );

  const hasEntityId = Boolean(entity?._id);

  return (
    <div className="marginTopM">
      {!readOnly && <DocumentUploadArea onFiles={addBuffered} accept={ACCEPT} />}

      {buffered.length > 0 ? (
        <div className="marginTopM">
          <div className="text-sm font-medium">
            {t("entityDocuments.bufferedLabel")}
          </div>
          {buffered.map((entry, idx) => (
            <div
              key={`${entry.file.name}-${idx}`}
              className="displayFlex alignItemsCenter gapM marginTopS"
            >
              <div className="flexGrow1 truncate" title={entry.file.name}>
                {entry.file.name}
              </div>
              <Select
                value={entry.role}
                onValueChange={(v) => setBufferedRole(idx, v)}
              >
                <SelectTrigger className="h-9 w-[170px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_KEYS.map((k) => (
                    <SelectItem key={k} value={k}>
                      {t(`assetPropertiesPanel.documents.role.${k}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={() => removeBuffered(idx)}
              >
                {t("global.delete")}
              </Button>
            </div>
          ))}
          <div className="marginTopM displayFlex alignItemsCenter gapM">
            <Button disabled={!hasEntityId || uploading} onClick={uploadBuffered}>
              {uploading
                ? t("entityDocuments.uploading")
                : t("entityDocuments.upload")}
            </Button>
            {!hasEntityId ? (
              <span className="text-sm text-muted-foreground">
                {t("entityDocuments.saveFirstHint")}
              </span>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="marginTopM">
        <DataTable
          columns={columns}
          data={rows}
          emptyMessage={t("assetPropertiesPanel.documents.empty")}
        />
      </div>

      <DocumentViewerDialog
        url={viewer?.url}
        name={viewer?.name ?? ""}
        kind={viewer?.kind ?? null}
        onClose={closeViewer}
      />
    </div>
  );
}
